import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { ProjectRenderer } from '../ProjectRenderer';
import {
	ExtrudeGeometry,
	Mesh,
	MeshBasicMaterial,
	Shape,
	Vector2,
	Vector2Tuple,
	Vector3,
} from 'three';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { RenderUtils } from '../utils/RenderUtils';
import RENDERER_CONFIG from '@/configs/rendererConfig';

const COLORS = RENDERER_CONFIG.COLORS;

const { MEASUREMENT } = RENDERER_CONFIG;

const initialLineMaterial = new LineMaterial({
	color: COLORS.RED,
	linewidth: MEASUREMENT.WIDTH,
});
const arrowHeadMaterial = new MeshBasicMaterial({ color: COLORS.RED });

const arrowHeadShape = new Shape();
arrowHeadShape.autoClose = true;
arrowHeadShape.moveTo(0, 0);
arrowHeadShape.lineTo(-0.15, -0.5);
arrowHeadShape.lineTo(0.15, -0.5);
const arrowHeadGeometry = new ExtrudeGeometry(arrowHeadShape, {
	depth: 0.01,
	bevelEnabled: false,
});
arrowHeadGeometry.rotateX(-(Math.PI / 2));

const POS_Y = MEASUREMENT.POS_Y;

export interface SerializedMeasurement {
	start: Vector2Tuple;
	end: Vector2Tuple;
}

export class Measurement {
	static readonly LAYER = MEASUREMENT.LAYER;

	id = crypto.randomUUID();
	private _visible = true;
	private _hovered = false;
	private line: Line2;
	private geometry = new LineGeometry();
	private startArrowHead: Mesh | null = null;
	private endArrowHead: Mesh | null = null;
	private start = new Vector2();
	private _end = new Vector2();
	private material = initialLineMaterial.clone();
	private arrowHeadMaterial = arrowHeadMaterial.clone();
	private position = new Vector2();
	private _isTemporary = true;
	private css2dObject!: CSS2DObject;
	private divElement!: HTMLDivElement;
	length = 0;

	constructor(
		private renderer: ProjectRenderer,
		start: Vector2Tuple,
		end: Vector2Tuple
	) {
		this.line = new Line2(this.geometry, this.material);
		this.line.layers.set(RENDERER_CONFIG.MEASUREMENT.LAYER);
		this.renderer.scene.add(this.line);

		this.initLabel();

		this.start.set(...start);
		this.end = new Vector2(...end);

		this.renderer.scene.add(this.line);
		this.createArrowHeads();
	}

	get hovered() {
		return this._hovered;
	}

	set hovered(value: boolean) {
		this._hovered = value;
		this.material.color.set(value ? COLORS.RED : COLORS.VIOLET);
		this.arrowHeadMaterial.color.set(value ? COLORS.RED : COLORS.VIOLET);
	}

	get visible() {
		return this._visible;
	}

	set visible(value: boolean) {
		this._visible = value;
		this.line.visible = value;
		this.startArrowHead!.visible = value;
		this.endArrowHead!.visible = value;
		this.css2dObject.visible = value;
	}

	get end() {
		return this._end;
	}

	set end(value: Vector2) {
		this._end.copy(value);
		this.compute();
	}

	get isTemporary() {
		return this._isTemporary;
	}

	set isTemporary(value: boolean) {
		this._isTemporary = value;
		this.material.color.set(value ? COLORS.RED : COLORS.VIOLET);
		this.arrowHeadMaterial.color.set(value ? COLORS.RED : COLORS.VIOLET);
	}

	private compute() {
		const direction = new Vector2()
			.subVectors(this.end, this.start)
			.normalize();

		const startAdjusted = new Vector2().addVectors(
			this.start,
			direction.clone().multiplyScalar(MEASUREMENT.LINE_OFFSET)
		);
		const endAdjusted = new Vector2().addVectors(
			this.end,
			direction.clone().multiplyScalar(-MEASUREMENT.LINE_OFFSET)
		);

		this.position = new Vector2()
			.addVectors(startAdjusted, endAdjusted)
			.multiplyScalar(0.5);

		this.geometry.setFromPoints([
			new Vector3(startAdjusted.x, POS_Y, startAdjusted.y),
			new Vector3(endAdjusted.x, POS_Y, endAdjusted.y),
		]);

		this.line.computeLineDistances();

		if (this.endArrowHead) {
			this.endArrowHead.position.set(this.end.x, POS_Y, this.end.y);
			this.endArrowHead.lookAt(this.start.x, POS_Y, this.start.y);
		}

		if (this.startArrowHead)
			this.startArrowHead.lookAt(this.end.x, POS_Y, this.end.y);

		this.length = this.start.distanceTo(this.end);
		this.divElement.innerText = `${Math.round(this.length * 100) / 100} m`;

		const perpendicular = new Vector2(-direction.y, direction.x);

		const labelPos = new Vector2().addVectors(
			this.position,
			perpendicular.multiplyScalar(MEASUREMENT.LABEL_TO_LINE_OFFSET)
		);
		this.css2dObject.position.set(labelPos.x, 0.5, labelPos.y);
	}

	private initLabel() {
		this.divElement = document.createElement('div');
		this.divElement.className = 'measure-label';

		this.css2dObject = new CSS2DObject(this.divElement);
		this.css2dObject.layers.set(Measurement.LAYER);

		this.css2dObject.position.set(this.position.x, 0.5, this.position.y);
		this.renderer.scene.add(this.css2dObject);
	}

	private createArrowHeads() {
		this.startArrowHead = new Mesh(arrowHeadGeometry, this.arrowHeadMaterial);
		this.startArrowHead.layers.set(Measurement.LAYER);

		this.startArrowHead.position.set(this.start.x, POS_Y, this.start.y);
		this.startArrowHead.lookAt(this.end.x, POS_Y, this.end.y);

		this.renderer.scene.add(this.startArrowHead);

		this.endArrowHead = new Mesh(arrowHeadGeometry, this.arrowHeadMaterial);
		this.endArrowHead.layers.set(Measurement.LAYER);

		this.endArrowHead.position.set(this.end.x, POS_Y, this.end.y);
		this.endArrowHead.lookAt(this.start.x, POS_Y, this.start.y);

		this.renderer.scene.add(this.endArrowHead);
	}

	export(): SerializedMeasurement {
		return {
			start: this.start.toArray(),
			end: this.end.toArray(),
		};
	}

	dispose() {
		this.css2dObject.element.remove();
		RenderUtils.disposeObject(this.line);
		RenderUtils.disposeObject(this.startArrowHead!);
		RenderUtils.disposeObject(this.endArrowHead!);
		RenderUtils.disposeObject(this.css2dObject);
	}
}
